import { useMemo, useEffect } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtNum } from "@/lib/format";
import { 
  Truck, Package, Scale, MapPin, Building2, 
  ChevronRight, ArrowRightLeft, Info, Search,
  Box, Map, ShieldCheck, Gauge
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TransporteRow {
  chave: string;
  numero: string;
  modFrete: string;
  transportadora: string;
  cnpjTransporta: string;
  placa: string;
  ufVeic: string;
  qtdVol: number;
  espVol: string;
  pesoL: number;
  pesoB: number;
  emissao: string;
}

const MODALIDADES_FRETE: Record<string, string> = {
  "0": "Contratação do Frete por conta do Remetente (CIF)",
  "1": "Contratação do Frete por conta do Destinatário (FOB)",
  "2": "Contratação do Frete por conta de Terceiros",
  "3": "Próprio por conta do Remetente",
  "4": "Próprio por conta do Destinatário",
  "9": "Sem Ocorrência de Transporte"
};

export default function TransportePage() {
  const { filteredNotas } = useFiscalStore();

  useEffect(() => { document.title = "Logística & Transporte — DataGrid XML"; }, []);

  const transportData = useMemo(() => {
    const rows: TransporteRow[] = [];
    let totalPesoB = 0;
    let totalVolumes = 0;
    const transps = new Set<string>();

    filteredNotas.forEach(n => {
      const transp = n.detalhes?.transporte as any;
      if (!transp) return;

      const modFrete = String(transp.modFrete || "9");
      const transportadora = transp.transporta?.xNome || "Próprio/Não Inf.";
      const cnpj = transp.transporta?.cnpjCpf || transp.transporta?.CNPJ || "";
      const placa = transp.veicTransp?.placa || "N/A";
      const uf = transp.veicTransp?.UF || "";
      
      const vol = transp.vol;
      let qVol = 0;
      let eVol = "";
      let pL = 0;
      let pB = 0;

      if (vol) {
        const vols = Array.isArray(vol) ? vol : [vol];
        vols.forEach((v: any) => {
          qVol += parseFloat(v.qVol) || 0;
          if (v.esp && !eVol) eVol = v.esp;
          pL += parseFloat(v.pesoL) || 0;
          pB += parseFloat(v.pesoB) || 0;
        });
      }

      rows.push({
        chave: n.chave,
        numero: n.numero,
        modFrete,
        transportadora,
        cnpjTransporta: cnpj,
        placa,
        ufVeic: uf,
        qtdVol: qVol,
        espVol: eVol || "Volume",
        pesoL: pL,
        pesoB: pB,
        emissao: n.emissao
      });

      totalPesoB += pB;
      totalVolumes += qVol;
      if (cnpj) transps.add(cnpj);
    });

    return { 
      rows: rows.sort((a, b) => b.emissao.localeCompare(a.emissao)),
      totalPesoB,
      totalVolumes,
      transpCount: transps.size
    };
  }, [filteredNotas]);

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-6 animate-in fade-in duration-1000">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-l-4 border-primary pl-8 py-4 mb-10 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Truck className="size-3 text-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">Logistics Intelligence Hub</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Logística <span className="text-primary">& Transporte</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
             <span className="flex items-center gap-2 uppercase tabular-nums"><Box className="size-3.5 text-primary" /> Volumes & Pesagem</span>
             <span className="flex items-center gap-2 uppercase tabular-nums"><MapPin className="size-3.5 text-emerald-500" /> Dados de Frota e Transportadoras</span>
          </p>
        </div>

        <div className="flex flex-col items-end gap-1">
           <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Total Processado</span>
           <div className="text-3xl font-black tabular-nums tracking-tighter">
              {fmtNum(transportData.rows.length)} <span className="text-sm text-primary">Fretes</span>
           </div>
        </div>
      </header>

      {/* KPI Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <TransportCard 
          title="Peso Bruto Total" 
          value={`${fmtNum(transportData.totalPesoB)} kg`} 
          icon={Scale} 
          color="text-primary" 
          subtitle="Carga Consolidada"
        />
        <TransportCard 
          title="Volumes Totais" 
          value={fmtNum(transportData.totalVolumes)} 
          icon={Package} 
          color="text-amber-500" 
          subtitle="Unidades de Transporte"
        />
        <TransportCard 
          title="Transportadoras" 
          value={fmtNum(transportData.transpCount)} 
          icon={Building2} 
          color="text-emerald-500" 
          subtitle="Parceiros Logísticos"
        />
        <TransportCard 
          title="Eficiência de Carga" 
          value={transportData.rows.length > 0 ? `${(transportData.totalPesoB / transportData.rows.length).toFixed(1)} kg` : "0 kg"} 
          icon={Gauge} 
          color="text-purple-500" 
          subtitle="Média por Nota"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Main List */}
        <div className="xl:col-span-2 glass-card border-primary/5 bg-muted/5 overflow-hidden">
           <div className="p-6 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
              <div className="flex items-center gap-3">
                 <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    <Search className="size-5" />
                 </div>
                 <div>
                    <h2 className="text-lg font-black tracking-tight uppercase">Rastreio de Documentos</h2>
                    <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">Detalhamento técnico de frete e veículos</p>
                 </div>
              </div>
           </div>

           <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-white/5 text-[9px] font-black uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="p-5">Nota / Data</th>
                    <th className="p-5">Transportadora</th>
                    <th className="p-5">Placa / UF</th>
                    <th className="p-5">Modalidade</th>
                    <th className="p-5 text-right">Peso (KG)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                   {transportData.rows.slice(0, 100).map((r, i) => (
                     <tr key={i} className="group hover:bg-white/[0.02] transition-colors">
                       <td className="p-5">
                          <div className="flex flex-col">
                             <span className="text-xs font-black tabular-nums text-foreground">#{r.numero}</span>
                             <span className="text-[9px] font-bold text-muted-foreground">{new Date(r.emissao).toLocaleDateString()}</span>
                          </div>
                       </td>
                       <td className="p-5">
                          <div className="flex flex-col">
                             <span className="text-xs font-bold truncate max-w-[180px] uppercase tracking-tight">{r.transportadora}</span>
                             <span className="text-[9px] font-black text-muted-foreground tabular-nums opacity-60">{r.cnpjTransporta || "—"}</span>
                          </div>
                       </td>
                       <td className="p-5">
                          <div className="flex items-center gap-2">
                             <div className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[10px] font-black tabular-nums">
                                {r.placa}
                             </div>
                             {r.ufVeic && (
                               <span className="text-[9px] font-black text-primary uppercase">{r.ufVeic}</span>
                             )}
                          </div>
                       </td>
                       <td className="p-5">
                          <div className="flex items-center gap-2 group/tip relative">
                             <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/20">
                                FRETE {r.modFrete}
                             </span>
                             <Info className="size-3 text-muted-foreground cursor-help opacity-40 group-hover/tip:opacity-100" />
                             <div className="absolute bottom-full left-0 mb-2 p-2 bg-black text-[8px] font-black text-white rounded-lg opacity-0 group-hover/tip:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50">
                                {MODALIDADES_FRETE[r.modFrete] || "Não Informado"}
                             </div>
                          </div>
                       </td>
                       <td className="p-5 text-right">
                          <div className="flex flex-col items-end">
                             <span className="text-xs font-black tabular-nums">{fmtNum(r.pesoB)}</span>
                             <span className="text-[9px] font-bold text-muted-foreground opacity-60">{r.qtdVol} {r.espVol}</span>
                          </div>
                       </td>
                     </tr>
                   ))}
                   {transportData.rows.length === 0 && (
                     <tr>
                        <td colSpan={5} className="p-32 text-center text-muted-foreground">
                           <div className="flex flex-col items-center gap-3 opacity-20">
                              <Map className="size-12" />
                              <span className="text-[10px] font-black uppercase tracking-widest">Nenhum dado de transporte detectado</span>
                           </div>
                        </td>
                     </tr>
                   )}
                </tbody>
              </table>
           </div>
        </div>

        {/* Sidebar Analytics */}
        <div className="space-y-6">
           <div className="glass-card p-8 border-emerald-500/5 bg-emerald-500/5 relative overflow-hidden">
              <h3 className="text-sm font-black uppercase tracking-widest mb-6 flex items-center gap-2">
                 <ShieldCheck className="size-4 text-emerald-500" /> Integridade Logística
              </h3>
              <div className="space-y-4">
                 <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-[10px] font-black text-muted-foreground uppercase">Frete CIF (Remetente)</span>
                    <span className="text-xs font-black tabular-nums">{transportData.rows.filter(r => r.modFrete === "0").length}</span>
                 </div>
                 <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-[10px] font-black text-muted-foreground uppercase">Frete FOB (Destinatário)</span>
                    <span className="text-xs font-black tabular-nums">{transportData.rows.filter(r => r.modFrete === "1").length}</span>
                 </div>
                 <div className="flex justify-between items-center py-2">
                    <span className="text-[10px] font-black text-muted-foreground uppercase">Sem Ocorrência</span>
                    <span className="text-xs font-black tabular-nums">{transportData.rows.filter(r => r.modFrete === "9").length}</span>
                 </div>
              </div>
           </div>

           <div className="glass-card p-8 border-primary/5 bg-primary/5">
              <h3 className="text-sm font-black uppercase tracking-widest mb-4">Exportar Logística</h3>
              <p className="text-[10px] font-bold text-muted-foreground/60 uppercase leading-relaxed mb-8">
                 Gere um relatório detalhado de volumes, pesos e transportadoras para controle de frete.
              </p>
              <button 
                className="w-full h-14 rounded-2xl bg-primary text-white font-black uppercase tracking-widest text-[10px] shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                 <ArrowRightLeft className="size-4" /> Exportar Dados de Frete
              </button>
           </div>
        </div>
      </div>
    </div>
  );
}

function TransportCard({ title, value, icon: Icon, color, subtitle }: any) {
  return (
    <div className="glass-card p-6 border-primary/5 bg-muted/5 hover:border-primary/20 transition-all group">
      <div className="flex justify-between items-start mb-4">
        <div className={cn("size-12 rounded-2xl flex items-center justify-center bg-white/5 border border-foreground/5 shadow-lg group-hover:rotate-6 transition-all", color)}>
          <Icon className="size-6" />
        </div>
      </div>
      
      <div className="space-y-1">
        <h3 className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">{title}</h3>
        <div className="text-2xl font-black tracking-tighter tabular-nums text-foreground">
          {value}
        </div>
        <p className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-tight">{subtitle}</p>
      </div>
    </div>
  );
}
